import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import CallTimer from "./calltimer.tsx"; // ✅ custom timer
import { formatTime, parseUntilObject } from "../../../utilities/conversions.js";

type Props = {
    baseUrl: string;
    currentUser: any;
};

declare global {
    interface Window {
        JitsiMeetExternalAPI: any;
    }
}

const VcomBookingCall: React.FC<Props> = ({
    baseUrl,
    currentUser,
}) => {
    const { eventKey } = useParams<{ eventKey: string }>();
    const [activeCallId, setActiveCallId] = useState(eventKey);
    const activeCallIdRef = useRef(activeCallId);
    const [loading, setLoading] = useState(true);
    const [callDetail, setCallDetail] = useState<any>(null);
    const [agent, setAgent] = useState<any>(null);
    const [customer, setCustomer] = useState<any>(null);
    const [alertBeepMessage, setAlertBeepMessage] = useState("");
    const [alertMinutesBefore, setAlertMinutesBefore] = useState(1);
    const wsServer = baseUrl.includes("localhost")
        ? "ws://localhost:8013/echo"
        : "wss://chamcore.com/echo";

    // keep ref synced
    useEffect(() => {
        activeCallIdRef.current = activeCallId;
    }, [activeCallId]);

    /** Jitsi Meet start */
    const startMeeting = (meetingId: string, dispName: string) => {
        const domain = "vcom.jablabla.com";

        // @ts-ignore
        const api = new window.JitsiMeetExternalAPI(domain, {
            roomName: meetingId,
            width: "100%",
            height: "100%",
            parentNode: jitsiContainerRef.current,
            userInfo: {
                displayName: dispName,
            },
            configOverwrite: {
                notifications: [
                    { type: "connection.CONNFAIL", disabled: true },
                    { type: "dialog.cameraConstraintFailedError", disabled: true },
                    { type: "dialog.cameraNotSendingData", disabled: true },
                    { type: "dialog.micNotSendingData", disabled: true },
                    { type: "notify.chatMessages", disabled: true },
                    { type: "notify.participantWantsToJoin", disabled: true },
                    { type: "notify.participantsWantToJoin", disabled: true },
                    { type: "notify.raisedHand", disabled: true },
                    { type: "notify.mutedTitle", disabled: true },
                    { type: "toolbar.noAudioSignalTitle", disabled: true },
                ],
                disableDeepLinking: true,
                prejoinPageEnabled: false,
                enableWelcomePage: false,
                toolbarButtons: ["camera", "toggle-camera", "microphone", "hangup"], // adjust to your needs
                buttonsWithNotifyClick: ["end-meeting", "hangup", "hangup-menu"],
            },
            interfaceConfigOverwrite: {
                TOOLBAR_ALWAYS_VISIBLE: true,
                SHOW_TIMER: false,
            },
        });

        // plain equivalent of the event listeners used in the original
        api.addListener('videoConferenceJoined', () => {
            // nothing to do straight away in our conversion — placeholder
            api.executeCommand('toggleTileView');
        });

        // 📌 Listeners
        api.addEventListeners({
            readyToClose: () => {
                localStorage.removeItem("call_timer_" + activeCallIdRef.current);
                if (jitsiContainerRef.current) {
                    jitsiContainerRef.current.innerHTML =
                        "<h1 style='color: white;display: flex;justify-content:center;align-items:center;height: 50vh;'>The call has been over. Thank you for reaching out.</h1>";
                }
            },
            participantJoined: () => {
                setShowWaitingModal(false); // hide modal
                // api.executeCommand('toggleTileView');
                // restore duration if needed
                const duration = localStorage.getItem("call_timer_" + activeCallIdRef.current);
                if (duration) {
                    const [m, s] = duration.split(":");
                    const totalSeconds = parseInt(m) * 60 + parseInt(s);
                    // 🔔 integrate with your CallTimer here
                    console.log("Resuming timer at:", totalSeconds);
                }
            },
            toolbarButtonClicked: (object: any) => {
                if (["end-meeting", "hangup", "hangup-menu"].includes(object.key)) {
                    console.log(object.key + " triggered");
                    endCall();
                }
            },
        });

        apiRef.current = api;
    };


    /** Fetch call details by callId */
    const fetchBooking = async () => {
        try {
            const res = await axios.get(`${baseUrl}/getBookingDetailAndInsertCall`, {
                params: { event_key: eventKey, token: currentUser?.token },
            });
            setCallDetail(res.data.callDetail);
            setAgent(res.data.agent);
            setCustomer(res.data.customer);
            setAlertBeepMessage(res.data.alert_beep_message);
            setAlertMinutesBefore(res.data.alert_minutes_before);
        } catch (err) {
            console.error("Error fetching call:", err);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {


        if (eventKey) fetchBooking();
    }, [eventKey, baseUrl, currentUser?.token]);

    // Jitsi + WebSocket refs and states
    const jitsiContainerRef = useRef<HTMLDivElement>(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);

    // 1. Load external_api.js dynamically
    useEffect(() => {
        if (window.JitsiMeetExternalAPI) {
            setScriptLoaded(true);
            return;
        }

        const script = document.createElement("script");
        script.src = "https://vcom.jablabla.com/external_api.js"; // your self-hosted Jitsi
        script.async = true;
        script.onload = () => {
            console.log("✅ Jitsi API script loaded");
            setScriptLoaded(true);
        };
        script.onerror = () => {
            console.error("Failed to load Jitsi external_api.js");
        };
        document.body.appendChild(script);
    }, []);
    const [showWaitingModal, setShowWaitingModal] = useState(true);
    const apiRef = useRef<any>(null);
    const wsRef = useRef<WebSocket | null>(null);

    const sendWSMessage = React.useCallback((msg: any) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(msg));
        } else {
            console.warn("⚠️ WebSocket is not open, cannot send:", msg);
        }
    }, []);
    /** End call handler */
    const endCall = React.useCallback(() => {
        var data = {
            call_history_id: callDetail?.id,
            about: "end_booking_call_now",
            channel: "common_channel",
            type: "video_call"
        };
        sendWSMessage(data);
        apiRef.current?.executeCommand("hangup");
        var formData = new FormData();
        formData.append("call_id", activeCallIdRef.current ?? "");
        formData.append("token", currentUser?.token);
        var duration: any = localStorage.getItem("call_timer_" + activeCallIdRef.current);
        var consumedDuration = (callDetail.duration * 60) - duration;
        formData.append("duration", formatTime(consumedDuration));
        localStorage.removeItem("call_timer_" + activeCallIdRef.current);
        let path = baseUrl.replace("/Api/ApiController", "");
        axios.get(path + `/Cronjobs/Booking_call_controller/finish_agent_call`, {
            params: { customersFlag: "yes", clientuid: callDetail.client_id, selectedLang: callDetail.language, extension: agent.code, event_id: callDetail.event_id, agent_id: agent.id, CallSid: callDetail.cid, DialCallDuration: callDetail.duration * 60, DialCallStatus: 'completed', From: callDetail.from_number, To: callDetail.to_number }
        }).then((res) => {
            console.log(res.data);
            console.log("Call ended.");
            window.close();
        });
    }, [callDetail?.id, sendWSMessage, currentUser.token, baseUrl]);
    /** WebSocket connection */
    useEffect(() => {
        const ws = new WebSocket(wsServer);
        wsRef.current = ws;
        ws.onmessage = (event) => {
            try {
                const data = parseUntilObject(event.data);
                if (data?.information?.customer_id === customer.uid) {
                    if (data.about === "end_booking_call_now") {
                        endCall();
                    }
                }
            } catch (e) {
                console.error("WS parse error", e);
            }
        };
        return () => {
            ws.close(); // cleanup old connection
        };
    }, [customer?.uid, wsServer, endCall]);

    /** Jitsi initialization */
    useEffect(() => {
        if (!scriptLoaded || !callDetail || !jitsiContainerRef.current) { setShowWaitingModal(false); return; }
        if (callDetail.call_status === "completed" || callDetail.call_status === "cancelled") { setShowWaitingModal(false); return; }

        if (!apiRef.current) {
            startMeeting(callDetail.cid, customer?.name || "Participant");
        }

        return () => {
            apiRef.current?.dispose();
        };
    }, [scriptLoaded, callDetail, customer?.name]);



    /** Buy minutes handler */
    const buyMoreMinutes = async (minutes: number) => {
        try {
            var formData = new FormData();
            formData.append("call_id", callDetail.id);
            formData.append("minute", minutes.toString());
            formData.append("type", "video");
            await axios.post(
                baseUrl + `/buyMoreMinutesInWebCall`,
                formData
            ).then(
                (res) => {
                    // extend timer in CallTimer
                    if (res.data.status === "success") {


                        var data = {
                            call_history_id: res.data.call_history_id,
                            about: "added_more_minutes",
                            channel: "common_channel",
                            type: "video_call"
                        };

                        sendWSMessage(data);

                        var formData = new FormData();
                        formData.append("call_id", callDetail?.id);
                        formData.append("token", currentUser?.token);
                        formData.append("full_capture", "yes");
                        formData.append("duration", formatTime(callDetail.duration * 60))

                        setActiveCallId(res.data.call_history_id);
                        callDetail.id = res.data.call_history_id;
                        callDetail.duration = minutes;

                        axios.post(baseUrl + `/captureVideoCallPayment`, formData).then((res) => {
                            console.log(res.data);
                        })
                    }
                }
            );
        } catch (err) {
            console.error(err);
        }
    };
    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen text-white">
                Loading call...
            </div>
        );
    }

    if (!callDetail) {
        return (
            <div className="flex items-center justify-center h-screen text-red-500">
                Call not found
            </div>
        );
    }

    return (
        <div className="bg-black h-screen">
            {callDetail.call_status === "completed" || callDetail.call_status === "cancelled" ? (
                <div className="flex items-center justify-center h-full">
                    <h1 className="text-white text-xl text-center">
                        The call has been over. Thank you for reaching out.
                    </h1>
                </div>
            ) : (
                <>
                    <div
                        ref={jitsiContainerRef}
                        id="jitsi-meet-conf-container"
                        className="h-full"
                    />
                    {/* ✅ Timer Component */}
                    {!showWaitingModal && (
                        <CallTimer
                            callId={activeCallId}
                            initialSeconds={callDetail.duration * 60}
                            alertMinutesBefore={alertMinutesBefore}
                            alertBeepMessage={alertBeepMessage}
                            onTimeEnd={endCall}
                            onBuyMinutes={buyMoreMinutes}
                        />
                    )}
                </>
            )}

            {/* ✅ Tailwind Modal (No Bootstrap) */}
            {showWaitingModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-orange-400 rounded-2xl p-6 w-80 text-center shadow-lg">
                        <p className="mt-4 text-black">
                            Please hold <br /> we are connecting
                        </p>
                        <p className="font-bold text-lg">{agent.name}</p>

                        {/* ✅ Tailwind loading spinner */}
                        <div className="flex justify-center mt-4">
                            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VcomBookingCall;
